import { clsx } from "clsx";
import { Download, Eye, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { Spinner } from "@/components/ui/Spinner";
import { PageHeader, SectionCard, StatTile, type TabItem } from "@/features/power_projects/components/ui";
import * as api from "../api";
import { useTitleReports } from "../hooks";
import type { LedBadges, LedJournalResult, LedParcel, LedParcelsPayload, LedSummary } from "../types";
import { JournalSplit } from "./JournalSplit";
import { LedMap } from "./LedMap";
import { RouteUploadPanel } from "./RouteUploadPanel";
import { FileChips, INPUT_SM, TONE_WAIT, fmtDate } from "./common";

/**
 * Sekcje widoku prowadzonego projektu współdzielone przez DWIE strony: kartę firmy
 * (/contractor/projects/:id, LedProjectPage) i warsztat ZE u nas (PowerProjectWorkspacePage
 * w trybie „projekt prowadzony przez firmę"). Nagłówek, kafelki i „Tytuły prawne do
 * potwierdzenia" wyglądają u nas tak jak u firmy — pod nimi u nas wracają karty warsztatu
 * (Sprawy, Działki…), a mapa i Pliki są w wersji z widoku firmy.
 */

export type LedTabKey = "map" | "parcels" | "register" | "tiles" | "files" | "memos" | "reports" | "journal" | "control";
export const DAY_OPTIONS = [7, 14, 30, 60, 90];

/** Zakładki widoku prowadzonego z licznikami z plakietek — ta sama lista u firmy i u nas. */
export function ledTabItems(b: LedBadges, journalRecent: number | undefined, withControl: boolean): TabItem<LedTabKey>[] {
  return [
    { key: "map", label: "Mapa" },
    // Licznik: tytuły do potwierdzenia + działki ze zmianą trasy do weryfikacji.
    { key: "parcels", label: "Działki i strony", count: (b.titles_pending + (b.route_review ?? 0)) || undefined, countTone: "warn" },
    { key: "register", label: "Rejestr spraw", count: b.register_unread || undefined, countTone: "warn" },
    { key: "tiles", label: "Etapy", count: b.tile_requests_pending || undefined, countTone: "warn" },
    { key: "files", label: "Pliki" },
    { key: "memos", label: "Notatki służbowe", count: b.memos_unread || undefined, countTone: "danger" },
    { key: "reports", label: "Raporty", count: (b.reports_overdue + b.reports_due_today) || undefined, countTone: b.reports_overdue ? "danger" : "warn" },
    { key: "journal", label: "Dziennik", count: journalRecent || undefined, countTone: "blue" },
    ...(withControl ? [{ key: "control" as LedTabKey, label: "Kontrola", count: b.titles_pending || undefined, countTone: "warn" as const }] : []),
  ];
}

/** Pasek widoczny TYLKO w podglądzie — inaczej łatwo zapomnieć, czemu zniknęły
 *  bursztynowe elementy i połowa dziennika. */
export function PreviewBar({ contractorName, onExit }: { contractorName: string; onExit: () => void }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 border border-[#1f3f8f] bg-[#eef2ff] px-3 py-2 text-[13px] dark:bg-[#11172e]">
      <span className="inline-flex items-center gap-1.5 bg-[#1f3f8f] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-white">
        <Eye size={11} /> Widok podwykonawcy
      </span>
      <span>
        Widzisz dokładnie to, co konta firmy <strong>{contractorName}</strong> — te same zakładki, mapa i dane,
        które przychodzą z serwera w ich wersji (nic nie jest ukrywane na ekranie). Zapis i decyzje są w tym trybie wyłączone.
      </span>
      <Button size="sm" variant="secondary" className="ml-auto" onClick={onExit}>
        Wróć do swojego widoku
      </Button>
    </div>
  );
}

/** Zgłoszone tytuły czekające na NASZE potwierdzenie — jedyna rzecz, która świeci. */
export function PendingTitles({ leadershipId, onOpen }: { leadershipId: string; onOpen: (parcelId: string) => void }) {
  const { data } = useTitleReports(leadershipId);
  const pending = (data ?? []).filter((r) => r.status === "pending");
  if (!pending.length) return null;
  return (
    <div className={clsx("mb-4 border", TONE_WAIT)} data-testid="pending-titles">
      <div className="border-b border-amber-400/60 px-4 py-2 font-mono text-[0.7rem] font-bold uppercase tracking-widest text-amber-800 dark:text-amber-300">
        Tytuły prawne do potwierdzenia · {pending.length}
      </div>
      <ul className="divide-y divide-amber-400/40">
        {pending.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-[13px]">
            <button type="button" className="font-mono text-xs font-bold hover:text-[var(--color-primary)] hover:underline" onClick={() => r.parcel_id && onOpen(r.parcel_id)}>
              {r.parcel_teryt}
            </button>
            <strong>{r.owner_name}</strong>
            <span className="text-xs text-[var(--color-text-secondary)]">
              {r.basis_code}{r.document_number && <> · {r.document_number}</>} · {r.reported_by_label} · {fmtDate(r.reported_at)}
            </span>
            <FileChips files={r.files} previewKind="led_title_file" />
            <Button size="sm" className="ml-auto" onClick={() => r.parcel_id && onOpen(r.parcel_id)}>Otwórz kartę</Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Wszystko PONAD zakładkami: nagłówek z przełącznikiem podglądu, pasek faktów (kto prowadzi,
 *  zamówienie, opiekun, od kiedy, trasa), kafelki i tytuły do potwierdzenia (u nas). */
export function LedProjectHeader({
  summary, internal, preview, onPreview, isContractor, onOpenParcel, backTo, backLabel,
}: {
  summary: LedSummary;
  internal: boolean;
  preview: boolean;
  onPreview: (v: boolean) => void;
  isContractor: boolean;
  onOpenParcel: (parcelId: string) => void;
  backTo: string;
  backLabel: string;
}) {
  const c = summary.counters;
  const b: LedBadges = summary.badges;
  const orderHref = summary.order_id ? (isContractor ? `/contractor/orders/${summary.order_id}` : `/orders/${summary.order_id}`) : null;
  const titlesDone = c.owners_total > 0 && c.owners_secured === c.owners_total;
  return (
    <>
      <PageHeader
        backTo={backTo}
        backLabel={backLabel}
        title={summary.name}
        subtitle={[summary.client_name, summary.branch_name, summary.sub_branch_name].filter(Boolean).join(" · ") || "Projekt prowadzony przez podwykonawcę"}
        actions={
          internal || preview ? (
            <label
              className={clsx(
                "inline-flex cursor-pointer select-none items-center gap-2 border px-2.5 py-1.5 text-[12px] transition-colors",
                preview
                  ? "border-[#1f3f8f] bg-[#1f3f8f] text-white"
                  : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[#1f3f8f] hover:text-[#1f3f8f]",
              )}
              title="Przełącz na dokładnie ten widok, który ma firma prowadząca (zakładki, mapa, dane z serwera w jej wersji)"
            >
              <input type="checkbox" className="sr-only" checked={preview} onChange={(e) => onPreview(e.target.checked)} />
              <Eye size={14} />
              Widok podwykonawcy
            </label>
          ) : undefined
        }
      />

      {preview && <PreviewBar contractorName={summary.contractor_name} onExit={() => onPreview(false)} />}

      {/* Jeden pasek faktów zamiast czterech kolumn rozstrzelonych na całą szerokość:
          kto prowadzi, na jakim zamówieniu, kto pilnuje i od kiedy. */}
      <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-[13px]">
        <span className="inline-flex items-baseline gap-1.5">
          <span className="section-label">prowadzi</span>
          <strong className="max-w-[24rem] truncate" title={summary.contractor_name}>{summary.contractor_name}</strong>
        </span>
        <span className="h-3.5 w-px bg-[var(--color-border)]" aria-hidden />
        <span className="inline-flex items-baseline gap-1.5">
          <span className="section-label">zamówienie</span>
          {summary.order_number && orderHref ? (
            <Link to={orderHref} className="inline-flex items-center gap-1 font-mono text-xs text-[var(--color-primary)] hover:underline">
              {summary.order_number} <ExternalLink size={11} />
            </Link>
          ) : (
            <span className="text-[var(--color-text-muted)]">—</span>
          )}
        </span>
        <span className="h-3.5 w-px bg-[var(--color-border)]" aria-hidden />
        <span className="inline-flex items-baseline gap-1.5">
          <span className="section-label">opiekun</span>
          <strong>{summary.caretaker_label || "—"}</strong>
        </span>
        <span className="h-3.5 w-px bg-[var(--color-border)]" aria-hidden />
        <span className="inline-flex items-baseline gap-1.5">
          <span className="section-label">przekazano</span>
          <span className="font-mono text-xs">{fmtDate(summary.handed_over_at)}</span>
        </span>
        {summary.route && (
          <>
            <span className="h-3.5 w-px bg-[var(--color-border)]" aria-hidden />
            <span className="inline-flex items-baseline gap-1.5">
              <span className="section-label">trasa</span>
              <span className="font-mono text-xs">{summary.route.version_label}</span>
              <span className="text-[11px] text-[var(--color-text-muted)]">z {fmtDate(summary.route.created_at)}</span>
            </span>
          </>
        )}
      </div>

      <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile label="Działki w trasie" value={c.parcels_in_route} hint={c.parcels_out_of_route ? `${c.parcels_out_of_route} poza trasą` : "wszystkie w trasie"} />
        <StatTile
          label="Tytuły prawne"
          value={`${c.owners_secured}/${c.owners_total}`}
          tone={titlesDone ? "ok" : c.owners_stale ? "warn" : "default"}
          hint={c.owners_stale ? `${c.owners_stale} na starej wersji trasy` : "pozyskane na aktualnej trasie"}
        />
        <StatTile label="Działki publiczne" value={c.parcels_public} hint="instytucje" />
        <StatTile label="Kafle" value={`${c.tiles_done}/${c.tiles_total}`} tone={c.tiles_total > 0 && c.tiles_done === c.tiles_total ? "ok" : "default"} hint="zrobione" />
        {internal && b.titles_pending > 0 ? (
          <StatTile label="Do potwierdzenia" value={b.titles_pending} tone="warn" hint="zgłoszone tytuły" />
        ) : (
          <StatTile
            label="Trasa"
            value={summary.route ? summary.route.version_label : "brak"}
            tone={summary.route ? "default" : "warn"}
            hint={summary.route ? `z ${fmtDate(summary.route.created_at)}` : "nie wgrano"}
          />
        )}
      </div>

      {internal && <PendingTitles leadershipId={summary.id} onOpen={onOpenParcel} />}
    </>
  );
}

/** Zakładka „Mapa" widoku prowadzonego: wgrywanie trasy, mapa z działkami, historia wersji. */
export function LedMapPanel({
  summary, parcelsData, parcelsLoading, tutorialHref, onSelectParcel,
}: {
  summary: LedSummary;
  parcelsData: LedParcelsPayload | undefined;
  parcelsLoading: boolean;
  tutorialHref: string;
  onSelectParcel: (p: LedParcel) => void;
}) {
  return (
    <div className="space-y-3">
      <RouteUploadPanel leadershipId={summary.id} tutorialHref={tutorialHref} hasRoute={!!summary.route} dxf={summary.route?.dxf ?? null} />
      <SectionCard
        title="Mapa projektu"
        action={
          <span className="text-[11px] text-[var(--color-text-muted)]">
            {summary.route
              ? <>wersja <strong className="font-mono">{summary.route.version_label}</strong> z {fmtDate(summary.route.created_at)}{summary.route.uploaded_by_label && <> · wgrał {summary.route.uploaded_by_label}</>}{summary.route.length_m != null && <> · {Math.round(summary.route.length_m)} m</>}</>
              : "trasa nie została jeszcze wgrana"}
          </span>
        }
        bodyClassName="p-3"
      >
        <p className="mb-2 text-[11px] text-[var(--color-text-muted)]">Kliknij działkę na mapie, żeby otworzyć jej kartę.</p>
        {parcelsLoading ? (
          <div className="flex h-40 items-center justify-center"><Spinner /></div>
        ) : (
          <LedMap
            route={parcelsData?.route ?? null}
            layers={parcelsData?.route_layers ?? null}
            parcels={parcelsData?.parcels ?? []}
            onSelect={onSelectParcel}
          />
        )}
      </SectionCard>
      {/* Historia wersji trasy: zmiana trasy przesuwa działki i unieważnia zgody,
          więc obie strony muszą widzieć, kiedy i po co ktoś ją podmienił. */}
      {(parcelsData?.route_history?.length ?? 0) > 0 && (
        <SectionCard title="Historia wersji trasy" bodyClassName="p-0">
          <ul className="divide-y divide-[var(--color-border-muted)]">
            {parcelsData!.route_history.map((v) => (
              <li key={v.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-2 text-[13px]">
                <span className="font-mono text-xs font-bold">{v.label}</span>
                {v.is_current && (
                  <span className="border border-[var(--color-primary)] px-1.5 py-px font-mono text-[10px] uppercase tracking-wider text-[var(--color-primary)]">
                    aktualna
                  </span>
                )}
                <span className="font-mono text-[11px] text-[var(--color-text-muted)]">{fmtDate(v.created_at)}</span>
                <span className="text-[var(--color-text-secondary)]">{v.author_label}</span>
                {v.length_m != null && (
                  <span className="font-mono text-[11px] text-[var(--color-text-muted)]">{Math.round(v.length_m)} m</span>
                )}
                {v.dxf?.url && (
                  <ManagedFileLink file={{ url: v.dxf.url, original_filename: v.dxf.name }} className="inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline">
                    <Download size={11} /> rysunek
                  </ManagedFileLink>
                )}
                <span className="basis-full text-[12px] text-[var(--color-text-secondary)]">
                  {v.comment || <span className="text-[var(--color-text-muted)]">bez komentarza autora</span>}
                </span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}
    </div>
  );
}

/** Zakładka „Dziennik" — okno dni, licznik świeżych, eksport CSV. Zapytanie trzyma rodzic
 *  (`journal`), bo liczbę świeżych wpisów pokazuje też licznik zakładki. */
export function LedJournalPanel({
  summary, internal, days, onDaysChange, journal, loading,
}: {
  summary: LedSummary;
  internal: boolean;
  days: number;
  onDaysChange: (d: number) => void;
  journal: LedJournalResult | undefined;
  loading: boolean;
}) {
  return (
    <SectionCard
      title="Dziennik projektu"
      action={
        <span className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="section-label">świeże wpisy z okna</span>
          <select
            className={clsx(INPUT_SM, "w-auto")}
            value={days}
            onChange={(e) => onDaysChange(Number(e.target.value))}
            aria-label="Okno dziennika"
          >
            {DAY_OPTIONS.map((d) => (
              <option key={d} value={d}>ostatnie {d} dni</option>
            ))}
          </select>
          <strong className="font-mono">{journal?.recent ?? 0}</strong>
          <button type="button" className="inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline" onClick={() => api.downloadJournalCsv(summary.id, 3650, summary.short_name)}>
            <Download size={12} /> eksport CSV
          </button>
        </span>
      }
    >
      {loading ? (
        <div className="flex h-40 items-center justify-center"><Spinner /></div>
      ) : (
        <JournalSplit
          rows={journal?.rows ?? []}
          internal={internal}
          contractorName={summary.contractor_name}
          handedOverAt={summary.handed_over_at}
        />
      )}
    </SectionCard>
  );
}
